/**
 * FindingsFilter — the shared status + severity filter for any finding
 * list surface (project findings index, scenario detail, persona detail).
 *
 * Keep the option sets + default values in lockstep with the main
 * findings-tab so the "exclude dismissed by default" rule and severity
 * ordering don't drift across surfaces.
 */

import { FilterPill } from "@/components/ui/filter-bar";
import { normalizeCanonicalStatus } from "@/components/finding-badges";

export type FindingsFilterStatus = "open" | "addressed" | "dismissed" | "all";
export type FindingsFilterSeverity =
  | "all"
  | "critical"
  | "major"
  | "minor"
  | "nit";

interface FindingsFilterProps {
  status: FindingsFilterStatus;
  severity: FindingsFilterSeverity;
  onStatusChange: (v: FindingsFilterStatus) => void;
  onSeverityChange: (v: FindingsFilterSeverity) => void;
  className?: string;
}

const STATUS_OPTIONS = [
  { value: "open" as const, label: "Open" },
  { value: "addressed" as const, label: "Fixed" },
  { value: "dismissed" as const, label: "Dismissed" },
  { value: "all" as const, label: "All" },
];

const SEVERITY_OPTIONS = [
  { value: "all" as const, label: "All severities" },
  { value: "critical" as const, label: "Critical" },
  { value: "major" as const, label: "Major" },
  { value: "minor" as const, label: "Minor" },
  { value: "nit" as const, label: "Nit" },
];

export function FindingsFilter({
  status,
  severity,
  onStatusChange,
  onSeverityChange,
  className,
}: FindingsFilterProps) {
  return (
    <div className={className ?? "flex flex-wrap items-center gap-3"}>
      <FilterPill<FindingsFilterStatus>
        ariaLabel="Filter by status"
        value={status}
        onValueChange={onStatusChange}
        options={STATUS_OPTIONS}
      />
      <FilterPill<FindingsFilterSeverity>
        ariaLabel="Filter by severity"
        value={severity}
        onValueChange={onSeverityChange}
        options={SEVERITY_OPTIONS}
      />
    </div>
  );
}

/**
 * Apply the filter predicates to a finding shape that has at least
 * `severity` and a status-like field. Supports both raw findings
 * (`triageStatus`) and canonicals (`status`).
 */
export function findingMatchesFilter(
  finding: {
    severity?: string | null;
    triageStatus?: string | null;
    status?: string | null;
    state?: string | null;
  },
  filter: { status: FindingsFilterStatus; severity: FindingsFilterSeverity },
): boolean {
  // Severity predicate
  if (filter.severity !== "all" && finding.severity !== filter.severity) {
    return false;
  }

  // Status predicate — same grouping as the main findings tab (lifecycle
  // state first, then the legacy status / raw triage values).
  const bucket = normalizeCanonicalStatus(
    finding.state ?? finding.status ?? finding.triageStatus ?? "open",
  );

  if (filter.status === "all") return true;
  return bucket === filter.status;
}
